/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * WalletDetailsCard
 *
 */

import React, { memo, useState, useEffect } from 'react';
import {
  Button,
  Text,
  Space,
  Toggle,
  Conditional,
  Popup,
} from '@cashfree-intl/coherent';
import CustomIcon from 'components/CustomIcons';
import { titleCase } from 'utils/common';
import { WALLET_RECHARGE_ACCOUNT_NAME } from 'utils/constants';
import { toggleStateText } from '../helpers';
import WalletStateChangeConfirmation from 'components/WalletStateChangeConfirmation/WalletStateChangeConfirmation';
import AddToWallet from 'components/AddToWallet/AddToWallet';
import { CURRENCY_SYMBOL } from 'utils/constants';
import { isWalletDisabled } from 'containers/Wallets/helpers';
import { ACTIVATE_REFUND_WALLET, ACTIVE, ACTIVE_FEE_WALLET_INFO, ADD_TO_WALLET, DEACTIVATE_REFUND_WALLET, HOLD, ON_HOLD_FREE_WALLET_INFO } from 'containers/Wallets/constants';
import { IWalletDetailsCards } from '../types';
import { StyledFeeWalletInfoText, StyledFeeWalletLowBalanceSpace, StyledWalletInfoText, StyledWalletPaper } from '../styled';
import InlineLoader from 'components/InlineLoader';
import { DISPUTE, FEE, IN_ACTIVE, MIN_BALANCE_AMOUNT, REFUND } from '../constants';

function WalletDetailsCards({
  walletType,
  walletData,
  walletBalance,
  handleRefreshClick,
  getWalletInfo,
  addMoneyClickHandler,
  handleToggleChange,
  bankDetails,
  bankList
}: IWalletDetailsCards) {
  const [activeModal, setActiveModal] = useState<string>('');
  const [active, setActive] = useState<boolean>(false);
  const [walletInactive, setWalletInactive] = useState(false);


  const onToggle = () => {
    if (active) {
      setActiveModal(DEACTIVATE_REFUND_WALLET);
    } else {
      setActiveModal(ACTIVATE_REFUND_WALLET);
    }
  };

  useEffect(() => {
    setActive(walletData.status === ACTIVE);
    setWalletInactive(walletData?.status === 'IN_ACTIVE');
  }, [walletData]);

  const closeModal = () => {
    setActiveModal('');
    getWalletInfo();
  };

  const handleModalConfirmation = () => {
    setActive(prev => !prev);
    handleToggleChange(active === true ? HOLD : ACTIVE);
    setActiveModal('');
  };

  const handleAddMoneyClick = () => {
    setActiveModal(ADD_TO_WALLET);
    addMoneyClickHandler();
  };
  return (
    <>
      <Conditional if={activeModal === ADD_TO_WALLET && Object.keys(bankDetails).length > 1}>
        <AddToWallet
          open={activeModal === ADD_TO_WALLET}
          walletType={`${titleCase(walletType)} Wallet`}
          closeModal={closeModal}
          bankDetails={{
            ...bankDetails,
            accountName: WALLET_RECHARGE_ACCOUNT_NAME,
          }}
          isWalletsIntro={false}
          getWalletInfo={getWalletInfo}
          bankList={bankList}
        />
      </Conditional>
      <Conditional
        if={[ACTIVATE_REFUND_WALLET, DEACTIVATE_REFUND_WALLET].includes(
          activeModal,
        )}
      >
        <WalletStateChangeConfirmation
          activeModal={activeModal}
          walletType={walletType}
          closeModal={closeModal}
          handleModalConfirmation={handleModalConfirmation}
        />
      </Conditional>
      <StyledWalletPaper>
        <Space justifyContent="space-between" className="mb-2">
          <Space alignItems='center' gap={2}>
            <Text variant="b14" color="bodyLight">
              Wallet Balance
            </Text>
            <Conditional if={walletType === FEE && walletBalance?.balance < MIN_BALANCE_AMOUNT}>
              <StyledFeeWalletLowBalanceSpace gap={1} alignItems="center">
                <CustomIcon
                  componentName="INFO"
                  fill="var(--DANGER)"
                  height="0.625rem"
                  width="0.625rem"
                />
                <Text color="danger" size="b12">
                  Low Balance
                </Text>
              </StyledFeeWalletLowBalanceSpace>
            </Conditional>
          </Space>
          <Conditional if={walletType !== FEE}>
            <Space gap={1}>
              <Text variant="b12">{toggleStateText(active)}</Text>
              <Toggle
                active={active}
                onToggle={onToggle}
                disabled={isWalletDisabled(walletBalance?.balance, active) || walletInactive || walletType === DISPUTE}
              />

            </Space>
          </Conditional>
          <Conditional if={walletType === FEE}>
            <Space gap={1} alignItems='center'>
              <Text variant="b12">Postpaid Billing</Text>
              <Conditional if={walletData?.status !== IN_ACTIVE}>
                <Popup
                  content={walletData?.status === ACTIVE ? ACTIVE_FEE_WALLET_INFO : ON_HOLD_FREE_WALLET_INFO}
                  trigger={
                    <span className="pointer">
                      <CustomIcon componentName="INFO" />
                    </span>
                  }
                  position="top center"
                />
              </Conditional>
            </Space>
          </Conditional>
        </Space>
        <Space justifyContent="space-between">
          <Space gap={3} alignItems="center">
            <InlineLoader
              loading={!Object.keys(walletBalance || {}).length || walletBalance?.balance === undefined}
              content={<Text variant="h28" color={active ? 'body' : 'placeholder'}>
                {CURRENCY_SYMBOL.INR} {walletBalance?.balance || '0'}
              </Text>}
            />
            <Space gap={1} alignItems="center" onClick={() => { handleRefreshClick(walletType) }} className='pointer'>
              <CustomIcon componentName="REFRESH" />
              <Text className="b12" color="primary">
                Refresh
              </Text>
            </Space>
          </Space>
          <Conditional if={walletType === FEE && walletData?.status === ACTIVE}>
            <StyledFeeWalletInfoText variant='b12'>All fee charges are deducted from your fee wallet.</StyledFeeWalletInfoText>
          </Conditional>
          <Conditional if={walletType !== FEE}>
            <Conditional if={!isWalletDisabled(walletBalance?.balance, active) || walletInactive}>
              <StyledWalletInfoText
                variant="b12"
                color={active ? 'body' : 'bodyLight'}
              >
                Refunds are processed from {active ? titleCase(walletType) : 'unsettled'} {active ? 'Wallet' : 'Balance'}.
              </StyledWalletInfoText>
            </Conditional>
            <Conditional if={isWalletDisabled(walletBalance?.balance, active) && !walletInactive && walletType !== DISPUTE}>
              <StyledWalletInfoText
                variant="b12"
                color="warning"
              >
                Recharge a minimum of {CURRENCY_SYMBOL.INR}5000 to activate the wallet.
              </StyledWalletInfoText>
            </Conditional>
          </Conditional>
        </Space>
        <Button
          primary
          size="small"
          className="mt-2"
          onClick={handleAddMoneyClick}
          disabled={walletInactive}
        >
          Add money
        </Button>
      </StyledWalletPaper>
    </>
  );
}

export default memo(WalletDetailsCards);
